import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  MapPin,
  Send,
  Share2,
  Bookmark,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProviderFavorite } from '../../contexts/ProviderFavoriteContext';
import {
  manproQueryKeys,
  mapProviderOpportunityToCard,
  providerOpportunityService,
  type OwnerProjectDocumentDto,
} from '../../api/manpro';
import { getOpportunityAction } from '../../lib/manproOpportunityMedia';
import { resolveDynamicImageUrl } from '../../lib/imageUrl';

const formatDate = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatCoordinate = (value?: number | null) =>
  typeof value === 'number' ? value.toFixed(6) : '-';

const documentCategoryLabels: Record<string, string> = {
  location_photo: 'Gallery Photo',
  drawing: 'Gambar Kerja / Denah',
  rab_boq: 'RAB / BOQ',
  specification: 'Spesifikasi / RKS',
  tender_document: 'Dokumen Tender',
  invitation_letter: 'Surat Undangan',
  offer_form: 'Form Penawaran',
  other: 'Dokumen Lainnya',
};

const getDocumentUrl = (document: OwnerProjectDocumentDto) => resolveDynamicImageUrl(document.file_path);

const ProviderOpportunityDetail = () => {
  const { opportunityId } = useParams();
  const { favoriteIds, toggleFavorite, isToggling } = useProviderFavorite();

  const opportunityQuery = useQuery({
    queryKey: manproQueryKeys.providerOpportunity(opportunityId || ''),
    // Detail Provider harus membaca endpoint MANPRO agar ID dari card API tidak dicari ke prototype lokal.
    queryFn: () => providerOpportunityService.getProviderOpportunity(opportunityId || ''),
    enabled: Boolean(opportunityId),
  });

  const otherProjectsQuery = useQuery({
    queryKey: ['manpro', 'provider', 'opportunities', 'other', opportunityId],
    queryFn: () => providerOpportunityService.getProviderOpportunities({ limit: 4 }),
  });

  const extractOtherProjects = (payload: any) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.data && Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const otherProjects = extractOtherProjects(otherProjectsQuery.data)
    .filter((item: any) => item.id !== opportunityId)
    .slice(0, 3)
    .map(mapProviderOpportunityToCard);

  if (opportunityQuery.isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-48 animate-pulse rounded bg-slate-100" />
        <section className="h-56 sm:h-72 animate-pulse rounded-3xl border border-slate-200 bg-white shadow-sm" />
        <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-2xl bg-white" />
              ))}
            </div>
            <div className="h-48 animate-pulse rounded-2xl bg-white" />
            <div className="h-56 animate-pulse rounded-2xl bg-white" />
          </div>
          <aside className="h-80 animate-pulse rounded-2xl bg-white" />
        </section>
      </div>
    );
  }

  if (opportunityQuery.isError || !opportunityQuery.data) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <FileText size={40} className="mx-auto text-slate-300" />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">Opportunity tidak ditemukan</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
          Data tidak dapat dimuat dari endpoint Provider MANPRO. Pastikan opportunity masih terbuka dan deadline belum lewat.
        </p>
        <Link to="/provider-opportunities" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white">
          Kembali ke Peluang Provider
        </Link>
      </div>
    );
  }

  const opportunity = opportunityQuery.data;
  const view = mapProviderOpportunityToCard(opportunity);
  const documents = view.documents;
  const primaryAction = getOpportunityAction(view, `/provider-opportunities/${view.id}/bid`);

  const overviewItems = [
    { label: 'Budget Owner', value: view.budgetLabel, icon: Wallet },
    { label: 'Kategori', value: view.category, icon: Building2 },
    { label: 'Tipe Pengadaan', value: view.procurementType, icon: Briefcase },
    { label: 'Sumber Proyek', value: view.sourceLabel, icon: ShieldCheck },
    { label: 'Status Bid Anda', value: view.hasBid ? 'Sudah bid' : 'Belum bid', icon: Users },
  ];

  const locationItems = [
    ['Alamat Lengkap', opportunity.address_detail || view.addressDetail],
    ['Provinsi', opportunity.province || '-'],
    ['Kota / Kabupaten', opportunity.city || view.location],
    ['Kecamatan', opportunity.district || '-'],
    ['Kode Pos', opportunity.postal_code || '-'],
    ['Koordinat', `${formatCoordinate(opportunity.latitude)}, ${formatCoordinate(opportunity.longitude)}`],
  ];

  const scheduleItems = [
    ['Kapan Ingin Dimulai', opportunity.metadata?.start_preference || '-'],
    ['Batas Akhir Penawaran', formatDate(opportunity.bid_deadline || opportunity.deadline)],
    ['Tanggal Klarifikasi', formatDate(opportunity.metadata?.clarification_date)],
    ['Tanggal Mulai Perkiraan', formatDate(opportunity.estimated_start)],
    ['Perkiraan Selesai', formatDate(opportunity.estimated_finish)],
    ['Durasi Pekerjaan', opportunity.duration || '-'],
    ['Metode Pengadaan', opportunity.procurement_method || '-'],
    ['Metode Pelaksanaan', opportunity.metadata?.execution_method || '-'],
    ['Jam Kerja', opportunity.metadata?.work_hours || '-'],
  ];

  const additionalItems = [
    ['Luas Area', opportunity.metadata?.area_size || '-'],
    ['Kondisi Saat Ini', opportunity.metadata?.current_condition || '-'],
    ['Prioritas', opportunity.metadata?.priority || '-'],
    ['Detail Kebutuhan', view.detailRequirements || opportunity.metadata?.detail_requirements || '-'],
    ['Owner', view.ownerName],
    ['Dibuat', formatDate(opportunity.created_at)],
    ['Diupdate', formatDate(opportunity.updated_at)],
  ];

  const googleMapsUrl = opportunity.google_maps_url || opportunity.metadata?.google_maps_url || null;

  const isBookmarked = view ? favoriteIds.includes(view.id) : false;

  const handleShare = () => {
    if (!view) return;
    const baseUrl = import.meta.env.VITE_BURSA_URL || `${import.meta.env.VITE_API_BASE_URL || window.location.origin}/bursa-proyek`;
    const shareUrl = `${baseUrl}/${view.id}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => toast.success('Link proyek disalin ke clipboard'))
      .catch(() => toast.error('Gagal menyalin link proyek'));
  };

  return (
    <div className="space-y-6">
      <Link to="/provider-opportunities" className="inline-flex items-center gap-2 text-sm font-extrabold text-blue-700">
        <ArrowLeft size={16} />
        Kembali ke Peluang Provider
      </Link>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative h-56 sm:h-72">
          <img src={view.coverImage} alt={view.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-transparent" />
          <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
            <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-blue-600 px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-extrabold text-white">
              <ShieldCheck size={14} className="shrink-0" />
              {view.sourceLabel}
            </span>
            <h1 className="mt-2 sm:mt-3 max-w-4xl break-words text-xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white line-clamp-2 sm:line-clamp-none">{view.title}</h1>
            <p className="mt-1.5 sm:mt-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-blue-50">
              <MapPin size={16} className="shrink-0" />
              <span className="line-clamp-1">{view.location}</span>
            </p>
          </div>
        </div>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
            {overviewItems.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.label} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <Icon size={21} className="text-blue-600" />
                  <p className="mt-3 text-xs font-bold text-slate-500">{item.label}</p>
                  <p className="mt-1 truncate text-sm font-extrabold text-slate-900">{item.value}</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden">
            <h2 className="text-xl font-extrabold text-slate-900">Deskripsi Proyek dari Owner</h2>
            <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-slate-600">{view.description}</p>
          </div>

          {view.galleryImages.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-extrabold text-slate-900">Gallery Lokasi</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {view.galleryImages.map((image, index) => (
                  <img
                    key={`${image.resolved_url}-${index}`}
                    src={image.resolved_url}
                    alt={image.name || `Gallery ${index + 1}`}
                    className="h-36 rounded-2xl object-cover"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Kebutuhan Owner</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {view.requirements.length ? (
                view.requirements.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 p-4">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                    <p className="min-w-0 break-words text-sm font-semibold leading-6 text-slate-600">{item}</p>
                  </div>
                ))
              ) : (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Owner belum mengisi kebutuhan khusus.</p>
              )}
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <DetailCard title="Lokasi Proyek" icon={MapPin} items={locationItems} />
            <DetailCard title="Jadwal Proyek" icon={Calendar} items={scheduleItems} />
          </div>

          <DetailCard title="Informasi Tambahan" icon={Clock} items={additionalItems} />
          
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Dokumen Pendukung</h2>
            <div className="mt-4 space-y-3">
              {documents.map((document) => (
                <a
                  key={document.id}
                  href={getDocumentUrl(document)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl bg-slate-50 p-3 transition hover:bg-blue-50 overflow-hidden"
                >
                  <p className="truncate text-sm font-extrabold text-slate-800">{document.name}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {documentCategoryLabels[String(document.document_category || 'other')] || 'Dokumen'}
                  </p>
                </a>
              ))}
              {documents.length === 0 && (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Owner belum mengunggah dokumen.</p>
              )}
            </div>
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-emerald-700"
              >
                <MapPin size={17} />
                Cek Google Maps
              </a>
            )}
          </div>
        </div>

        <aside className="min-w-0 w-full space-y-4 lg:sticky lg:top-24 lg:w-[340px]">

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <h2 className="font-extrabold text-slate-900">Siap Mengajukan Bid?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Gunakan data lengkap dari Owner untuk menyusun harga, durasi, metode kerja, dan catatan komersial.
            </p>
            {view.isRedirectOpportunity ? (
              <a
                href={view.sourceUrl || '#'}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700"
              >
                <Send size={17} />
                {primaryAction.label}
              </a>
            ) : view.hasBid || view.isDeadlinePassed ? (
              <Link
                to={view.hasBid ? '/provider-bids' : '/provider-opportunities'}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-200 px-4 py-3 text-sm font-extrabold text-slate-600"
              >
                <Wallet size={17} />
                {view.hasBid ? 'Lihat Bid Anda' : 'Bid Ditutup'}
              </Link>
            ) : (
              <Link
                to={`/provider-opportunities/${view.id}/bid`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700"
              >
                <Send size={17} />
                Kirim Bid
              </Link>
            )}
            
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
              >
                <Share2 size={17} />
                Bagikan
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(view.id)}
                disabled={isToggling}
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition ${
                  isBookmarked
                    ? 'border-blue-200 bg-blue-50 text-blue-600'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                } disabled:opacity-50`}
              >
                <Bookmark size={17} className={isBookmarked ? 'fill-blue-600' : ''} />
                {isBookmarked ? 'Tersimpan' : 'Simpan'}
              </button>
            </div>
            
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Proyek Lainnya</h2>
            <div className="mt-4 space-y-4">
              {otherProjectsQuery.isLoading ? (
                <div className="space-y-4">
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
                </div>
              ) : otherProjects.length > 0 ? (
                otherProjects.map((project: any) => (
                  <Link
                    key={project.id}
                    to={`/provider-opportunities/${project.id}`}
                    className="block rounded-xl border border-slate-100 bg-white p-3 transition hover:border-blue-200 hover:bg-blue-50 hover:shadow-md"
                  >
                    <h3 className="line-clamp-2 break-words text-sm font-bold text-slate-800">{project.title}</h3>
                    <div className="mt-2 flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-500">
                      <Building2 size={14} className="shrink-0 text-slate-400" />
                      <span className="truncate min-w-0 flex-1">{project.category}</span>
                    </div>
                    <div className="mt-1.5 flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-500">
                      <MapPin size={14} className="shrink-0 text-slate-400" />
                      <span className="truncate min-w-0 flex-1">{project.location || project.addressDetail || '-'}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Tidak ada proyek lain.</p>
              )}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

type DetailCardProps = {
  title: string;
  icon: LucideIcon;
  items: string[][];
};

const DetailCard = ({ title, icon: Icon, items }: DetailCardProps) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden">
    <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
      <Icon size={20} className="shrink-0 text-blue-600" />
      <span className="truncate">{title}</span>
    </h2>
    <div className="mt-5 grid gap-4 md:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <p className="text-xs font-extrabold text-slate-500">{label}</p>
          <p className="mt-1 break-words text-sm font-semibold leading-6 text-slate-800">{value || '-'}</p>
        </div>
      ))}
    </div>
  </div>
);

export default ProviderOpportunityDetail;

